import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, Input, Modal, Select, Typography, message } from 'antd';

import { getUsersByIds, searchCollaborators, searchUsersByEmail } from '../../services/users';
import { normalizeId } from '../../utils/ids';
import UserChip from './UserChip';
import InvitedTag from './InvitedTag';
import { inviteByEmail } from '../../services/sharing';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const MIN_PREFIX = 3;
const DEBOUNCE_MS = 300;

/**
 * A dropdown option: the platform's user chip plus the disambiguating email.
 * The chip is NON-interactive here — an option's whole job is to be selected,
 * and a button inside it would swallow that click to open a modal instead.
 */
export function UserOptionRow({ user }) {
	return (
		<span className="user-search-option">
			<UserChip user={user} interactive={false} />
			<InvitedTag user={user} />
			{user?.email ? (
				<Typography.Text type="secondary" className="user-search-option__email">
					{user.email}
				</Typography.Text>
			) : null}
		</span>
	);
}

/**
 * Server-backed user picker for sharing surfaces. Privacy model (matches the
 * API): a 3+ character prefix searches only people you already collaborate
 * with; anyone else must be found by their EXACT email address. No browsing
 * of the user base.
 *
 * Props mirror antd Select where it matters: value/onChange (userId string,
 * or array in mode="multiple"), placeholder, disabled, excludeIds.
 */
export default function UserSearchSelect({
	value,
	onChange,
	mode,
	placeholder = 'Search collaborators, or type a full email address',
	disabled = false,
	excludeIds = [],
	size,
	style,
	className,
	// Offered to platform admins only (the API refuses everyone else): a full
	// email nobody has an account for gets an Invite action.
	canInvite = false,
}) {
	const [results, setResults] = useState([]);
	const [knownUsers, setKnownUsers] = useState({}); // id -> public user, for value labels
	const [searching, setSearching] = useState(false);
	const [term, setTerm] = useState('');
	const [searchedTerm, setSearchedTerm] = useState('');
	const [inviteOpen, setInviteOpen] = useState(false);
	const [inviting, setInviting] = useState(false);
	const [inviteForm] = Form.useForm();
	const debounceRef = useRef(null);
	const requestSeq = useRef(0);

	// Resolve labels for ids already selected (edit flows, round-trips).
	const valueIds = useMemo(
		() => (Array.isArray(value) ? value : value ? [value] : []).map(normalizeId).filter(Boolean),
		[value]
	);
	useEffect(() => {
		// `in` (not truthiness): unresolved ids cache as null so a deleted or
		// unreadable user is fetched once, not on every knownUsers change.
		const missing = valueIds.filter(id => !(id in knownUsers));
		if (!missing.length) return;
		let active = true;
		getUsersByIds(missing).then(users => {
			if (!active) return;
			setKnownUsers(prev => {
				const next = { ...prev };
				missing.forEach(id => { if (!(id in next)) next[id] = null; });
				users.forEach(u => { next[normalizeId(u._id)] = u; });
				return next;
			});
		}).catch(() => {});
		return () => { active = false; };
	}, [valueIds, knownUsers]);

	const runSearch = useCallback(term => {
		const trimmed = (term || '').trim();
		const seq = ++requestSeq.current;

		let request = null;
		if (EMAIL_RE.test(trimmed)) {
			request = searchUsersByEmail(trimmed);
		} else if (trimmed.length >= MIN_PREFIX) {
			request = searchCollaborators(trimmed);
		}

		setSearchedTerm('');
		if (!request) {
			setResults([]);
			setSearching(false);
			return;
		}

		setSearching(true);
		request
			.then(users => {
				if (seq !== requestSeq.current) return; // a newer search superseded this one
				setResults(users);
				setKnownUsers(prev => {
					const next = { ...prev };
					users.forEach(u => { next[normalizeId(u._id)] = u; });
					return next;
				});
			})
			.catch(() => {
				if (seq === requestSeq.current) setResults([]);
			})
			.finally(() => {
				if (seq === requestSeq.current) {
					setSearching(false);
					setSearchedTerm(trimmed);
				}
			});
	}, []);

	const handleSearch = useCallback(text => {
		setTerm(text || '');
		if (debounceRef.current) clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => runSearch(text), DEBOUNCE_MS);
	}, [runSearch]);

	const trimmedTerm = term.trim();
	const offerInvite = canInvite && !searching && EMAIL_RE.test(trimmedTerm)
		&& searchedTerm === trimmedTerm && results.length === 0;

	const submitInvite = async ({ firstName, lastName }) => {
		setInviting(true);
		try {
			const invite = await inviteByEmail({ email: trimmedTerm, firstName: firstName.trim(), lastName: lastName.trim() });
			const user = {
				_id: invite.userId, email: invite.email, firstName: firstName.trim(), lastName: lastName.trim(),
				fullName: `${firstName.trim()} ${lastName.trim()}`, invited: true,
			};
			setKnownUsers(prev => ({ ...prev, [normalizeId(user._id)]: user }));
			setResults([user]);
			setInviteOpen(false);
			inviteForm.resetFields();
			message.success(`Invitation sent to ${invite.email}`);
			if (onChange) onChange(mode === 'multiple' ? [...valueIds, normalizeId(user._id)] : normalizeId(user._id));
		} catch (err) {
			message.error(err?.response?.data?.message || 'Could not send the invitation.');
		} finally {
			setInviting(false);
		}
	};

	useEffect(() => () => {
		if (debounceRef.current) clearTimeout(debounceRef.current);
	}, []);

	const excluded = useMemo(() => new Set((excludeIds || []).map(normalizeId).filter(Boolean)), [excludeIds]);
	const options = useMemo(() => {
		const byId = new Map();
		// Selected values first (so they always have labels), then results.
		valueIds.forEach(id => {
			const user = knownUsers[id];
			byId.set(id, { value: id, label: <UserOptionRow user={user || { _id: id, fullName: id }} /> });
		});
		results.forEach(user => {
			const id = normalizeId(user._id);
			if (!id || excluded.has(id)) return;
			byId.set(id, { value: id, label: <UserOptionRow user={user} /> });
		});
		return [...byId.values()];
	}, [results, valueIds, knownUsers, excluded]);

	return (
		<>
		<Select
			showSearch
			size={size}
			mode={mode}
			value={value}
			onChange={onChange}
			onSearch={handleSearch}
			filterOption={false}
			loading={searching}
			options={options}
			placeholder={placeholder}
			disabled={disabled}
			allowClear
			style={style}
			className={className}
			notFoundContent={searching
				? 'Searching…'
				: offerInvite
					? (
						<div className="user-search-invite">
							<Typography.Text type="secondary">{`No keepr account for ${trimmedTerm}.`}</Typography.Text>
							<Button type="primary" onMouseDown={e => e.preventDefault()} onClick={() => setInviteOpen(true)}>
								{`Invite ${trimmedTerm}`}
							</Button>
						</div>
					)
					: 'Type 3+ letters to search people you collaborate with, or a full email address to invite anyone.'}
		/>
		{canInvite ? (
			<Modal
				open={inviteOpen}
				title="Invite a person"
				okText="Send invitation"
				cancelText="Cancel"
				confirmLoading={inviting}
				onOk={() => inviteForm.submit()}
				onCancel={() => setInviteOpen(false)}
				destroyOnClose
			>
				<Typography.Paragraph type="secondary">{`${trimmedTerm} gets an email to set up an account. You can share with them now.`}</Typography.Paragraph>
				<Form form={inviteForm} layout="vertical" onFinish={submitInvite} requiredMark={false}>
					<Form.Item name="firstName" label="First name" rules={[{ required: true, whitespace: true, message: 'Enter a first name' }]}>
						<Input autoComplete="off" />
					</Form.Item>
					<Form.Item name="lastName" label="Last name" rules={[{ required: true, whitespace: true, message: 'Enter a last name' }]}>
						<Input autoComplete="off" />
					</Form.Item>
				</Form>
			</Modal>
		) : null}
		</>
	);
}
